import React, { useState } from "react";
import { GET_MAC } from "../queries/macQueries";
import DeviceDetailBase from "../components/DeviceDetailBase";
import {
  enableRemoteDesktop,
  disableRemoteDesktop,
  updateDeviceInventory,
  getAvailableSoftwareUpdates,
  refreshMacUserList,
  deleteMacUser,
  unlockMacUser,
  repairMacManagedAdminPassword,
} from "../commands/mdmCommands.js";
import RestartDeviceModal from '../components/modals/RestartDeviceModal.jsx';
import InstallProfileModal from '../components/modals/InstallProfileModal.jsx';
import RenameDeviceModal from '../components/modals/RenameDeviceModal.jsx';
import ShutdownDeviceModal from '../components/modals/ShutdownDeviceModal.jsx';
import LockDeviceModal from '../components/modals/LockDeviceModal.jsx';
import EraseDeviceModal from '../components/modals/EraseDeviceModal.jsx';
import PinHistoryTable from "../components/PinHistoryTable.jsx";
import ActivationLockBypassModal from '../components/modals/ActivationLockBypassModal.jsx';
import BluetoothModal from '../components/modals/BluetoothModal.jsx';
import { calculateStoragePercentage, formatMacAddress } from '../components/DeviceDetailBase';
import MacWallpaperStatusPanel from '../components/MacWallpaperStatusPanel.jsx';

export default function MacDetail() {
  const [showRestartDeviceModal, setShowRestartDeviceModal] = useState(false);
  const [showInstallProfileModal, setShowInstallProfileModal] = useState(false);
  const [showRenameDeviceModal, setShowRenameDeviceModal] = useState(false);
  const [showShutdownDeviceModal, setShowShutdownDeviceModal] = useState(false);
  const [showLockDeviceModal, setShowLockDeviceModal] = useState(false);
  const [showEraseDeviceModal, setShowEraseDeviceModal] = useState(false);
  const [showPinHistory, setShowPinHistory] = useState(false);
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [showBluetoothModal, setShowBluetoothModal] = useState(false);

  const actionButtons = [
    {
      label: "Check for available software updates",
      onClick: (device) => getAvailableSoftwareUpdates(device.UDID),
    },
    {
      label: "Erase Device",
      capability: "canErase",
      onClick: () => setShowEraseDeviceModal(true),
    },
    {
      label: "Install Profile",
      capability: "canInstallProfile",
      onClick: () => setShowInstallProfileModal(true),
    },
    {
      label: "Lock Device",
      capability: "canLock",
      onClick: () => setShowLockDeviceModal(true),
    },
    {
      label: "Activation-Lock-Bypass",
      capability: "canActivationLockBypass",
      hideForUserEnrollment: true,
      onClick: () => setShowBypassModal(true),
    },
    {
      label: "Bluetooth",
      capability: "canBluetooth",
      requiresSupervised: true,
      hideForUserEnrollment: true,
      onClick: () => setShowBluetoothModal(true),
    },
    {
      label: "Remote Desktop",
      capability: "canRemoteDesktop",
      hideForUserEnrollment: true,
      onClick: (device) => device.SecurityInfo.RemoteDesktopEnabled
        ? disableRemoteDesktop(device.UDID)
        : enableRemoteDesktop(device.UDID),
      getLabel: (device) => `${device.SecurityInfo.RemoteDesktopEnabled ? "Disable" : "Enable"} Remote Desktop`
    },
    {
      label: "Restart Device",
      capability: "canRestart",
      onClick: () => setShowRestartDeviceModal(true),
    },
    {
      label: "Rename Device",
      onClick: () => setShowRenameDeviceModal(true),
    },
    {
      label: "Shutdown Device",
      capability: "canShutdown",
      requiresSupervised: true,
      hideForUserEnrollment: true,
      onClick: () => setShowShutdownDeviceModal(true),
    },
    {
      label: "Lokale Benutzer aktualisieren",
      hideForUserEnrollment: true,
      onClick: (device) => refreshMacUserList(device.UDID),
    },
    {
      label: "Managed-Admin-Passwort reparieren",
      hideForUserEnrollment: true,
      onClick: (device) => {
        const password = window.prompt(
          "Neues Passwort für den Managed Admin (leer = Passwort aus dem DEP-Profil verwenden):",
          ""
        );
        if (password === null) return;
        return repairMacManagedAdminPassword(device.UDID, password.trim() || undefined);
      },
    },
  ];

  const infoSections = [
    {
      title: 'hardware',
      getData: (device) => ({
        "serial number": device.SerialNumber,
        "architecture": device.QueryResponses.IsAppleSilicon ? "Apple Silicon" : "Intel",
        "product name": device.QueryResponses.ProductName,
        "model number": device.QueryResponses.ModelNumber,
        "storage": calculateStoragePercentage(device),
        "enrolled via DEP": device.SecurityInfo?.EnrolledViaDEP
          ?? device.enrollmentContext?.hasDepAssignment,
        "User Enrollment": device.SecurityInfo?.IsUserEnrollment,
        "DEP supervised (Soll)": device.enrollmentContext?.depIsSupervised == null
          ? "—"
          : (device.enrollmentContext.depIsSupervised ? "ja" : "nein"),
        "FileVault encryption": device.SecurityInfo.FDE_Enabled,
        ...(device.unlockPins?.length > 0 && {
          "unlock PIN": showPinHistory ? (
            <PinHistoryTable
              data={device.unlockPins}
              hideShowPinHistoryTable={() => setShowPinHistory(false)}
            />
          ) : (
            <div onClick={() => setShowPinHistory(true)}>click to view</div>
          )
        })
      })
    },
    {
      title: 'network',
      getData: (device) => ({
        "hostname": device.QueryResponses.HostName,
        "local hostname": device.QueryResponses.LocalHostName,
        "WiFi MAC address": formatMacAddress(device.QueryResponses.WiFiMAC),
        "ethernet MAC address": formatMacAddress(device.QueryResponses.EthernetMAC),
        "bluetooth MAC address": formatMacAddress(device.QueryResponses.BluetoothMAC),
        "firewall": device.SecurityInfo.FirewallEnabled,
        "firewall: block all incoming traffic": device.SecurityInfo.BlockAllIncoming,
      })
    },
    {
      title: 'operating system',
      getData: (device) => ({
        "macOS version": device.QueryResponses.OSVersion,
        "build version": device.QueryResponses.BuildVersion,
        "MDM profile installed": device.mdmProfileInstalled,
        "SIP enabled": device.QueryResponses.SystemIntegrityProtectionEnabled,
        "supervised": device.QueryResponses.IsSupervised,
        "check for updates": device.QueryResponses.OSUpdateSettings.AutoCheckEnabled,
        "download updates in background": device.QueryResponses.OSUpdateSettings.BackgroundDownloadEnabled,
        "install macOS updates": device.QueryResponses.OSUpdateSettings.AutomaticOSInstallationEnabled,
        "install App Store updates": device.QueryResponses.OSUpdateSettings.AutomaticAppInstallationEnabled,
        "install security updates": device.QueryResponses.OSUpdateSettings.AutomaticSecurityUpdatesEnabled,
        "remote desktop": device.SecurityInfo.RemoteDesktopEnabled,
        "Bluetooth": device.bluetoothEnabled === true
          ? "an"
          : device.bluetoothEnabled === false ? "aus" : "—",
        "Activation-Lock-Bypass": device.activationLockBypassCode?.code ? "vorhanden" : "—",
      })
    },
    {
      title: 'Mac-Hintergrund',
      getData: (device) => ({
        Auslieferung: <MacWallpaperStatusPanel udid={device.UDID} />,
      }),
    },
    {
      title: "lokale Benutzer",
      getData: (device) => {
        const users = Array.isArray(device.LocalUsers) ? device.LocalUsers : [];
        if (!users.length) {
          return {
            Hinweis: "Noch keine UserList — Inventur oder „Lokale Benutzer aktualisieren“.",
            Stand: device.LocalUsersUpdatedAt
              ? new Date(device.LocalUsersUpdatedAt).toLocaleString()
              : "—",
          };
        }
        const rows = {};
        users.forEach((user) => {
          const label = user.FullName
            ? `${user.FullName} (${user.UserName})`
            : user.UserName;
          rows[label] = (
            <span className="d-inline-flex flex-wrap gap-1 align-items-center">
              <span className="small text-muted">
                UID {user.UID ?? "—"}
                {user.IsLoggedIn ? " · angemeldet" : ""}
                {user.HasSecureToken ? " · SecureToken" : ""}
                {user.MobileAccount ? " · Mobile" : ""}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => unlockMacUser(device.UDID, user.UserName)}
              >
                Entsperren
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => {
                  if (!window.confirm(`Benutzer „${user.UserName}“ wirklich löschen?`)) return;
                  deleteMacUser(device.UDID, user.UserName, { forceDeletion: true });
                }}
              >
                Löschen
              </button>
            </span>
          );
        });
        rows.Stand = device.LocalUsersUpdatedAt
          ? new Date(device.LocalUsersUpdatedAt).toLocaleString()
          : "—";
        return rows;
      },
    },
  ];

  const renderModals = (device, configProfiles, onCommandIssued) => (
    <>
      {showRestartDeviceModal && (
        <RestartDeviceModal
          visible={showRestartDeviceModal}
          UDID={device.UDID}
          hideRestartDeviceModal={() => setShowRestartDeviceModal(false)}
        />
      )}
      {showInstallProfileModal && (
        <InstallProfileModal
          visible={showInstallProfileModal}
          UDID={device.UDID}
          currentProfiles={device.Profiles}
          configProfiles={configProfiles}
          hideInstallProfileModal={() => setShowInstallProfileModal(false)}
        />
      )}
      {showRenameDeviceModal && (
        <RenameDeviceModal
          visible={showRenameDeviceModal}
          UDID={device.UDID}
          platform='macOS'
          oldName={device.QueryResponses.DeviceName}
          hideRenameDeviceModal={() => setShowRenameDeviceModal(false)}
        />
      )}
      {showShutdownDeviceModal && (
        <ShutdownDeviceModal
          visible={showShutdownDeviceModal}
          UDID={device.UDID}
          hideShutdownDeviceModal={() => setShowShutdownDeviceModal(false)}
        />
      )}
      {showLockDeviceModal && (
        <LockDeviceModal
          visible={showLockDeviceModal}
          UDID={device.UDID}
          hideLockDeviceModal={() => setShowLockDeviceModal(false)}
        />
      )}
      {showEraseDeviceModal && (
        <EraseDeviceModal
          visible={showEraseDeviceModal}
          UDID={device.UDID}
          platform="mac"
          device={device}
          configProfiles={configProfiles || []}
          hideEraseDeviceModal={() => setShowEraseDeviceModal(false)}
        />
      )}
      {showBypassModal && (
        <ActivationLockBypassModal
          visible={showBypassModal}
          device={device}
          hideModal={() => setShowBypassModal(false)}
          onIssued={onCommandIssued}
        />
      )}
      {showBluetoothModal && (
        <BluetoothModal
          visible={showBluetoothModal}
          device={device}
          hideModal={() => setShowBluetoothModal(false)}
          onIssued={onCommandIssued}
        />
      )}
    </>
  );

  return (
    <DeviceDetailBase
      query={GET_MAC}
      deviceType="mac"
      getDeviceData={(data) => data.mac}
      actionButtons={actionButtons}
      renderModals={renderModals}
      infoSections={infoSections}
      onRefresh={(device) => updateDeviceInventory("mac", device.UDID)}
    />
  );
}
